import '../../email';
import '../../header';
import '../../toast';

import { Carousel } from '../../carousel';
import { initCartComponent } from '../../cart';

const RESIZE_DEBOUNCE_DELAY = 100;

function loadAthletesData() {
    const container = document.querySelector<HTMLElement>('.js-athlete-shop-container');
    if (container === null) {
        throw new Error('.js-athlete-shop-container element is null');
    }
    const data = JSON.parse(container.dataset.athletesShop as string);
    delete container.dataset.athletesShop;
    return data;
}

type Image = {
    filename: string;
    alt_text: string | null;
    srcsets: {[key: string]: string};
    jpeg: {[key: string]: string};
    webp: {[key: string]: string};
    avif: {[key: string]: string};
}

type Product = {
    product_id: string;
    name: string
    price: number
    url: string
    image: Image
};

type Athlete = {
    slug: string;
    name: string;
    first_name: string;
    last_name: string;
    shop_url: string
    shop_id: string | null;
    profile_img: Image;
    profile_img_preview: Image;
    brand_imgs: Array<Image>;
    products: Array<Product>;
};

function generateAthleteListItems(athletesData: Array<Athlete>) {
    if (!athletesData || athletesData.length === 0) {
        return;
    }
    
    // Generate dropdown items
    const dropdownList = document.querySelector('.js-athlete-dropdown-list');
    if (dropdownList === null) {
        throw new Error('.js-athlete-dropdown-list element is null');
    }
    dropdownList.innerHTML = athletesData.map(athlete => `
        <div class="shop-by-athlete__list-item shop-by-athlete__dropdown-item js-athlete-dropdown-item" 
             data-action="select-dropdown" 
             data-athlete-name="${athlete.name}">
            <span class="shop-by-athlete__athlete-name js-athlete-name">${athlete.name}</span>
            <picture>
                <source srcset="${athlete.profile_img_preview.srcsets.avif}" type="image/avif"/>
                <source srcset="${athlete.profile_img_preview.srcsets.webp}" type="image/webp"/>
                <source srcset="${athlete.profile_img_preview.srcsets.jpeg}" type="image/jpeg"/>
                <img src="${athlete.profile_img_preview.webp['320']}" alt="${athlete.profile_img_preview.alt_text}" class="shop-by-athlete__athlete-img"/>
            </picture>
        </div>
    `).join('');
    
    // Generate main list items
    const listContainer = document.querySelector('.js-athlete-list-container');
    if (listContainer === null) {
        throw new Error('.js-athlete-list-container element is null');
    }
    listContainer.innerHTML = athletesData.map((athlete, index) => `
        <label for="athlete${index + 1}" class="shop-by-athlete__list-item js-athlete-item">
            <span class="shop-by-athlete__athlete-name js-athlete-name" 
                  data-action="show-detail" 
                  data-athlete-name="${athlete.name}">${athlete.name}</span>
            <picture>
                <source srcset="${athlete.profile_img_preview.srcsets.avif}" type="image/avif"/>
                <source srcset="${athlete.profile_img_preview.srcsets.webp}" type="image/webp"/>
                <source srcset="${athlete.profile_img_preview.srcsets.jpeg}" type="image/jpeg"/>
                <img src="${athlete.profile_img_preview.webp['320']}" alt="${athlete.profile_img_preview.alt_text}" class="shop-by-athlete__athlete-img"/>
            </picture>
        </label>
    `).join('');
}

function generateAthleteShopHTML(athlete: Athlete) {
    const headerHTML = `
        <div class="shop-by-athlete__header-mobile-container">
            <div class="shop-by-athlete__header-mobile">
                <div class="shop-by-athlete__header-content shop-by-athlete__header-content--active js-athlete-header-content js-active-shop-header" data-athlete="${athlete.name}">
                    <div class="shop-by-athlete__header-profile">
                        <div>
                            <picture>
                                <source srcset="${athlete.profile_img.srcsets.avif}" type="image/avif"/>
                                <source srcset="${athlete.profile_img.srcsets.webp}" type="image/webp"/>
                                <source srcset="${athlete.profile_img.srcsets.jpeg}" type="image/jpeg"/>
                                <img src="${athlete.profile_img.webp['320']}" alt="${athlete.profile_img.alt_text}"/>
                            </picture>
                        </div>
                    </div>
                    <div class="shop-by-athlete__header-title">
                        <div class="shop-by-athlete__header-profile-content">
                            <p class="shop-by-athlete__header-profile-title">${athlete.name.toUpperCase()}</p>
                        </div>
                        <div class="shop-by-athlete__header-profile-brands">
                            ${athlete.brand_imgs.map(brand_img => `
                                <picture>
                                    <source srcset="${brand_img.srcsets.avif}" type="image/avif"/>
                                    <source srcset="${brand_img.srcsets.webp}" type="image/webp"/>
                                    <source srcset="${brand_img.srcsets.jpeg}" type="image/jpeg"/>
                                    <img class="shop-by-athlete__header-profile-brand-icon" src="${brand_img.webp['120']}" alt="${brand_img.alt_text}"/>
                                </picture>
                            `).join('')}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `;

    const productsHTML = athlete.products.map(product => `
        <a data-shop-product-id="${product.product_id}" data-shop-product class="product" href="${product.url}" target="_blank">
            <div class="product__image-container">
                <picture>
                    <source srcset="${product.image.srcsets.avif}" type="image/avif"/>
                    <source srcset="${product.image.srcsets.webp}" type="image/webp"/>
                    <source srcset="${product.image.srcsets.jpeg}" type="image/jpeg"/>
                    <img src="${product.image.webp['320']}" alt="${product.image.alt_text}" class="product__image"/>
                </picture>
            </div>
            <div class="product__info">
                <div class="product__name">${product.name}</div>
                <div class="product__price">${product.price}</div>
            </div>
        </a>
    `).join('');

    const discoverHTML = `
        <div class="product">
            <div class="shop-by-athlete__discover-image-container">
                <picture>
                    <source srcset="${athlete.profile_img_preview.srcsets.avif}" type="image/avif"/>
                    <source srcset="${athlete.profile_img_preview.srcsets.webp}" type="image/webp"/>
                    <source srcset="${athlete.profile_img_preview.srcsets.jpeg}" type="image/jpeg"/>
                    <img src="${athlete.profile_img_preview.webp['320']}" alt="${athlete.profile_img_preview.alt_text}" class="shop-by-athlete__discover-image"/>
                </picture>
                <div class="shop-by-athlete__overlay">
                    <div class="spacer--flex-1"></div>
                    <div class="shop-by-athlete__overlay-text">Discover more on</div>
                    <div class="spacer--flex-2"></div>
                    <a href="${athlete.shop_url}">
                        <button class="btn--athlete-overlay btn--red btn--white-font">
                            ${athlete.first_name}'s Store
                        </button>
                    </a>
                </div>
            </div>
        </div>
    `;

    const gridHTML = `
        <p class="products__affiliate-disclaimer products__affiliate-disclaimer-padded-top">Products may generate commissions.</p>
        <div class="shop-by-athlete__products-grid shop-by-athlete__products-grid--active js-products-grid js-active-products-grid" data-athlete="${athlete.name}">
            ${productsHTML}
            ${discoverHTML}
        </div>
    `;

    return headerHTML + gridHTML;
}

function scrollToActiveAthlete() {
    const activeListItem = document.querySelector<HTMLElement>('.js-active-athlete');
    if (!activeListItem) {
        return;
    }
    const container = document.querySelector('.js-athlete-list-container');
    if (container === null) {
        throw new Error('.js-athlete-list-container element is null');
    }

    // Calculate target scroll position
    let targetScrollTop;
    if (container === activeListItem.offsetParent || container.contains(activeListItem.offsetParent)) {
        targetScrollTop = activeListItem.offsetTop;
    } else {
        const containerRect = container.getBoundingClientRect();
        const itemRect = activeListItem.getBoundingClientRect();
        targetScrollTop = itemRect.top - containerRect.top + container.scrollTop;
        const containerStyles = window.getComputedStyle(container);
        const paddingTop = parseFloat(containerStyles.paddingTop) || 0;
        targetScrollTop -= paddingTop;
    }
    
    const itemHeight = activeListItem.offsetHeight;
    const containerHeight = container.clientHeight;
    const containerScrollTop = container.scrollTop;
    
    // Check if item is not fully visible
    if (targetScrollTop < containerScrollTop || 
        targetScrollTop + itemHeight > containerScrollTop + containerHeight) {
        container.scrollTo({
            top: targetScrollTop, behavior: 'smooth'
        });
    }
}

function scrollToSelectedDropdownItem() {
    const selectedDropdownItem = document.querySelector<HTMLElement>('.js-selected-dropdown-athlete');
    if (selectedDropdownItem === null) {
        throw new Error('.js-selected-dropdown-athlete element is null');
    }
    const dropdownList = document.querySelector<HTMLElement>('.js-athlete-dropdown-list');
    if (dropdownList === null) {
        throw new Error('.js-athlete-dropdown-list element is null');
    }
    const dropdownRect = dropdownList.getBoundingClientRect();
    const itemRect = selectedDropdownItem.getBoundingClientRect();
    
    // Calculate relative position
    const relativeTop = itemRect.top - dropdownRect.top + dropdownList.scrollTop;
    const itemHeight = selectedDropdownItem.offsetHeight;
    const dropdownHeight = dropdownList.clientHeight;
    const dropdownScrollTop = dropdownList.scrollTop;
    
    // Check if item is not fully visible
    if (relativeTop < dropdownScrollTop || 
        relativeTop + itemHeight > dropdownScrollTop + dropdownHeight) {
        dropdownList.scrollTo({
            top: relativeTop, behavior: 'smooth'
        });
    }
}

const scrollToSelected = () => {
    scrollToActiveAthlete();
    scrollToSelectedDropdownItem();
};

function setSelectedAthlete(athleteName: string, athletesData: Array<Athlete>) {
    document
        .querySelectorAll('.js-athlete-item')
        .forEach((item) => {
            item.classList.remove('shop-by-athlete__list-item--active', 'js-active-athlete');
            const itemAthleteNameElem = item.querySelector('.js-athlete-name');
            if (itemAthleteNameElem === null) {
                throw new Error('js-athlete-name element is null');
            }
            const itemAthleteName = itemAthleteNameElem.textContent;
            if (itemAthleteName === athleteName) {
                item.classList.add('shop-by-athlete__list-item--active', 'js-active-athlete');
            }
        });
    document.querySelectorAll<HTMLElement>('.js-athlete-dropdown-item').forEach((item) => {
        item.classList.remove('shop-by-athlete__dropdown-item--selected', 'js-selected-dropdown-athlete');
        const itemAthleteNameElem = item.querySelector('.js-athlete-name');
        if (itemAthleteNameElem === null) {
            throw new Error('js-athlete-name element is null');
        }
        const itemAthleteName = itemAthleteNameElem.textContent;
        if (itemAthleteName === athleteName) {
            item.classList.add('shop-by-athlete__dropdown-item--selected', 'js-selected-dropdown-athlete');
        }
    });
    document.querySelectorAll<HTMLElement>('.js-products-grid').forEach((grid) => {
        grid.classList.remove('shop-by-athlete__products-grid--active', 'js-active-products-grid');
        if (grid.dataset.athlete === athleteName) {
            grid.classList.add('shop-by-athlete__products-grid--active', 'js-active-products-grid');
        }
    });
    document.querySelectorAll<HTMLElement>('.js-athlete-header-content').forEach((header) => {
        header.classList.remove('shop-by-athlete__header-content--active', 'js-active-shop-header');
        if (header.dataset.athlete === athleteName) {
            header.classList.add('shop-by-athlete__header-content--active', 'js-active-shop-header');
        }
    });

    const selectedAthlete = athletesData.find(athlete => athlete.name === athleteName) as Athlete;
    const container = document.querySelector('.js-athlete-shop-container');
    if (container === null) {
        throw new Error('.js-athlete-shop-container element is null');
    }
    container.innerHTML = generateAthleteShopHTML(selectedAthlete);

    // Auto-scroll after selection
    requestAnimationFrame(scrollToSelected);
}

function toggleDropdown() {
    const button = document.querySelector('.js-athlete-dropdown-toggle');
    if (button === null) {
        throw new Error('.js-athlete-dropdown-toggle element is null');
    }
    const list = document.querySelector('.js-athlete-dropdown-list');
    if (list === null) {
        throw new Error('.js-athlete-dropdown-list element is null');
    }
    button.classList.toggle('shop-by-athlete__dropdown-button--open');
    const isOpen = list.classList.toggle('shop-by-athlete__dropdown-list--open');
    if (isOpen) {
        requestAnimationFrame(scrollToSelectedDropdownItem);
    }
}

const selectDropdownItem = (athletesData: Array<Athlete>, athleteName: string, element: Element) => {
    document.querySelectorAll('.js-athlete-dropdown-item').forEach((item) => {
        item.classList.remove('shop-by-athlete__dropdown-item--selected', 'js-selected-dropdown-athlete');
    });
    element.classList.add('shop-by-athlete__dropdown-item--selected', 'js-selected-dropdown-athlete');
    const button = document.querySelector('.js-athlete-dropdown-toggle');
    if (button === null) {
        throw new Error('.js-athlete-dropdown-toggle element is null');
    }
    const list = document.querySelector('.js-athlete-dropdown-list');
    if (list === null) {
        throw new Error('.js-athlete-dropdown-list element is null');
    }
    button.classList.remove('shop-by-athlete__dropdown-button--open');
    list.classList.remove('shop-by-athlete__dropdown-list--open');
    setSelectedAthlete(athleteName, athletesData);
};

function handleOutsideClick(event: MouseEvent) {
    const dropdown = document.querySelector('.js-athlete-dropdown');
    if (dropdown && !dropdown.contains((event.target as HTMLElement))) {
        const button = document.querySelector('.js-athlete-dropdown-toggle');
        const list = document.querySelector('.js-athlete-dropdown-list');
        if (button && list) {
            button.classList.remove('shop-by-athlete__dropdown-button--open');
            list.classList.remove('shop-by-athlete__dropdown-list--open');
        }
    }
}

function initializeLandingPage() {
    // Initialize the cart
    initCartComponent();

    // Mount all the carousels
    Array.from(document.querySelectorAll<HTMLElement>('.js-carousel')).forEach(element => { new Carousel(element); });

    const athletesData = loadAthletesData();
    generateAthleteListItems(athletesData);

    const handleClick = (event: MouseEvent) => {
        const target = (event.target as HTMLElement);
        const action = target.closest<HTMLElement>('[data-action]')?.dataset.action;
        
        if (!action) {
            return;
        }
        
        switch (action) {
            case 'toggle-dropdown':
                toggleDropdown();
                break;
            case 'select-dropdown': {
                const dropdownElement = target.closest<HTMLElement>('[data-action="select-dropdown"]');
                if (dropdownElement === null) {
                    throw new Error('[data-action="select-dropdown"] element is null');
                }
                const dropdownAthleteName = dropdownElement.dataset.athleteName as string;
                selectDropdownItem(athletesData, dropdownAthleteName, dropdownElement);
                break;
            }
            case 'show-detail': {
                const detailElement = target.closest<HTMLElement>('[data-action="show-detail"]');
                if (detailElement === null) {
                    throw new Error('[data-action="show-detail"] element is null');
                }
                const detailAthleteName = detailElement.dataset.athleteName as string;
                setSelectedAthlete(detailAthleteName, athletesData);
                break;
            }
            default:
                break;
        }
    };

    // Initialize first athlete selection
    const firstAthlete = document.querySelector('.js-athlete-item');
    if (firstAthlete) {
        const firstAthleteNameElem = firstAthlete.querySelector('.js-athlete-name');
        if (firstAthleteNameElem === null) {
            throw new Error('.js-athlete-name element is null');
        }
        const firstAthleteName = firstAthleteNameElem.textContent;
        setSelectedAthlete(firstAthleteName, athletesData);
    }

    document.addEventListener('click', handleClick);
    document.addEventListener('click', handleOutsideClick);

    let resizeTimeout: number;
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = window.setTimeout(() => {
            requestAnimationFrame(scrollToSelected);
        }, RESIZE_DEBOUNCE_DELAY);
    });
}

initializeLandingPage();

if (module.hot) {
    module.hot.accept();
}
